import {match} from 'ts-pattern';

import {isEmpty, isNil, isNotEmpty, isNotNil} from 'ramda';

import {Nullish} from 'shared/web';

export interface InspectionReportVerdict {
  headline: string;
  reason: string;
}

export interface InspectionReportListItem {
  lead: string;
  text: string;
}

export type InspectionReportBlock =
  | {type: 'paragraph'; text: string}
  | {type: 'list'; items: InspectionReportListItem[]};

export interface InspectionReportSection {
  title: string;
  blocks: InspectionReportBlock[];
}

export interface InspectionReportConclusion {
  eyebrow: string;
  headline: string;
  text: string;
}

export interface InspectionReportModel {
  verdict: InspectionReportVerdict | null;
  sections: InspectionReportSection[];
  conclusion: InspectionReportConclusion | null;
}

const REPORT_START = '<article';
const REPORT_CLASS = 'ca-report';
const XHTML_NAMESPACE = 'http://www.w3.org/1999/xhtml';
// Text inside these elements is never readable copy, so it is dropped together with the element.
const DROPPED_ELEMENTS = new Set(['script', 'style', 'template', 'noscript']);
const WHITESPACE = /\s+/gu;
const TAG = /<[^<>]*>/gu;
const TYPOGRAPHIC_QUOTES = /[“”„‟″]/gu;

// Foreign subtrees (`<svg>`, `<math>`) keep lowercase names and can carry their own scripts and
// styles, so they are dropped whole together with the listed HTML elements.
const isDroppedElement = (node: Node) =>
  node instanceof Element &&
  (node.namespaceURI !== XHTML_NAMESPACE || DROPPED_ELEMENTS.has(node.localName));

const readText = (node: Node): string =>
  match(node)
    .when(
      (current) => current.nodeType === Node.TEXT_NODE,
      (current) => current.textContent ?? ''
    )
    .when(isDroppedElement, () => '')
    .otherwise((current) => Array.from(current.childNodes).map(readText).join(''));

// Reports typed through a word processor arrive with typographic quotes around attribute values
// (`class=“ca-report”`), which HTML does not treat as quotes. Straighten them inside tags only, so
// quotes in the copy itself stay untouched.
const straightenTagQuotes = (html: string) =>
  html.replace(TAG, (tag) => tag.replace(TYPOGRAPHIC_QUOTES, '"'));

const normalizeText = (text: string) => text.replace(WHITESPACE, ' ').trim();

const getText = (node: Node | Nullish) => (isNotNil(node) ? normalizeText(readText(node)) : '');

const findChild = (parent: Element, tagName: string, className: string) =>
  Array.from(parent.children).find(
    (child) => child.tagName === tagName && child.classList.contains(className)
  );

const parseListItem = (item: Element) => {
  const lead = findChild(item, 'B', 'ca-item__lead');
  const text = Array.from(item.childNodes)
    .filter((child) => child !== lead)
    .map(readText)
    .join('');

  return {lead: getText(lead), text: normalizeText(text)};
};

const parseBlock = (element: Element): InspectionReportBlock | null =>
  match(element.tagName)
    .with('P', () => {
      const text = getText(element);

      return isEmpty(text) ? null : {type: 'paragraph' as const, text};
    })
    .with('UL', () => {
      const items = Array.from(element.children)
        .filter((child) => child.tagName === 'LI')
        .map(parseListItem)
        .filter((item) => isNotEmpty(item.lead) || isNotEmpty(item.text));

      return isEmpty(items) ? null : {type: 'list' as const, items};
    })
    .otherwise(() => null);

const parseSection = (section: Element) => {
  const body = findChild(section, 'DIV', 'ca-section__body') ?? section;
  const blocks = Array.from(body.children).map(parseBlock).filter(isNotNil);

  return {title: getText(findChild(section, 'H2', 'ca-section__title')), blocks};
};

const parseVerdict = (header: Element | Nullish) => {
  if (isNil(header)) {
    return null;
  }
  const verdict = {
    headline: getText(findChild(header, 'H2', 'ca-verdict__headline')),
    reason: getText(findChild(header, 'P', 'ca-verdict__reason')),
  };

  return isEmpty(verdict.headline) && isEmpty(verdict.reason) ? null : verdict;
};

const parseConclusion = (footer: Element | Nullish) => {
  if (isNil(footer)) {
    return null;
  }
  const conclusion = {
    eyebrow: getText(findChild(footer, 'P', 'ca-cta__eyebrow')),
    headline: getText(findChild(footer, 'H2', 'ca-cta__headline')),
    text: getText(findChild(footer, 'P', 'ca-cta__text')),
  };

  return isEmpty(conclusion.headline) && isEmpty(conclusion.text) ? null : conclusion;
};

/**
 * Reads the mechanic's `ca-report` HTML into plain text fields. Only the known report structure is
 * read and every value is text, so nothing from the HTML (tags, attributes, styles, scripts, links)
 * reaches the DOM. Returns `null` for anything that is not a `ca-report`, so the caller can fall back
 * to the plain-text comment.
 */
export function parseInspectionReport(html: string | Nullish): InspectionReportModel | null {
  if (isNil(html) || !html.trimStart().startsWith(REPORT_START)) {
    return null;
  }

  const body = new DOMParser().parseFromString(straightenTagQuotes(html), 'text/html').body;
  const article = body.firstElementChild;
  if (body.childElementCount !== 1 || isNil(article) || !article.classList.contains(REPORT_CLASS)) {
    return null;
  }

  const report = {
    verdict: parseVerdict(findChild(article, 'HEADER', 'ca-verdict')),
    sections: Array.from(article.children)
      .filter((child) => child.tagName === 'SECTION' && child.classList.contains('ca-section'))
      .map(parseSection)
      .filter((section) => isNotEmpty(section.title) || isNotEmpty(section.blocks)),
    conclusion: parseConclusion(findChild(article, 'FOOTER', 'ca-cta')),
  };

  const isReportEmpty =
    isNil(report.verdict) && isEmpty(report.sections) && isNil(report.conclusion);

  return isReportEmpty ? null : report;
}
